import type { UIMessage } from '@ai-sdk/react'
import {
  Message,
  MessageActions,
  MessageContent,
  MessageResponse,
} from '@neo/blocks/ai-elements/message'
import { Shimmer } from '@neo/blocks/ai-elements/shimmer'
import { Source, Sources, SourcesContent, SourcesTrigger } from '@neo/blocks/ai-elements/sources'
import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { getToolName, isToolUIPart } from 'ai'
import { Check, ChevronDown, FileText, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getTextContent, getToolLabel, isToolPart, type Part } from './utils'

interface AssistantMessageProps {
  message: UIMessage
  /** True only for the in-flight assistant message while Buddy is still working. */
  isActive?: boolean
}

const ReasoningPart = ({ part }: { part: Extract<Part, { type: 'reasoning' }> }) => {
  const [expanded, setExpanded] = useState(false)
  const isStreaming = part.state === 'streaming'

  return (
    <div className="text-xs-400 text-muted-foreground">
      {isStreaming ? (
        <Shimmer as="span" className="text-xs-400">
          Thinking...
        </Shimmer>
      ) : (
        <button
          type="button"
          className="flex items-center gap-1.5 hover:text-foreground transition-colors"
          onClick={() => setExpanded((v) => !v)}
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? '' : '-rotate-90'}`} />
          <span>Thought</span>
        </button>
      )}
      {expanded && (
        <div className="mt-1 pl-4.5 text-xs-400 text-muted-foreground italic whitespace-pre-wrap">
          {part.text}
        </div>
      )}
    </div>
  )
}

const ToolCallPart = ({ part }: { part: Part }) => {
  if (!isToolUIPart(part)) return null

  const toolName = getToolName(part)
  const isDone = part.state === 'output-available'
  const isWaiting = part.state === 'input-available'
  const output = isDone ? (part.output as Record<string, unknown>) : null
  const isError =
    part.state === 'output-error' || (output && 'success' in output && output.success === false)

  if (isWaiting) return null

  if (isDone || isError) {
    // Nullable: a tool with no human wording renders nothing at all, and a mapped
    // tool can opt out of the finished line with `done: null` so Buddy's own
    // sentence is the only outcome the customer reads. Success and failure share
    // the same label; only the icon and the trailing word differ.
    const finishedLabel = getToolLabel(toolName, true)
    if (!finishedLabel) return null
    return (
      <div className="flex items-center gap-1.5 text-xs-400 text-muted-foreground">
        <span>{isError ? `${finishedLabel} failed` : finishedLabel}</span>
        <span>
          {isError ? (
            <X className="w-4 h-4 text-status-danger" />
          ) : (
            <Check className="w-4 h-4 text-status-success" />
          )}
        </span>
      </div>
    )
  }

  const label = getToolLabel(toolName, false)
  if (!label) return null

  return (
    <div className="py-0.5">
      <Shimmer as="span" className="text-xs-400">
        {label}
      </Shimmer>
    </div>
  )
}

const SourcesSection = ({ sources }: { sources: Extract<Part, { type: 'source-url' }>[] }) => (
  <Sources defaultOpen>
    <SourcesTrigger count={sources.length} />
    <SourcesContent>
      {sources.map((source) => (
        <Source key={source.sourceId} href={source.url} title={source.title || source.url} />
      ))}
    </SourcesContent>
  </Sources>
)

const SourceDocumentPart = ({ part }: { part: Extract<Part, { type: 'source-document' }> }) => (
  <div className="inline-flex items-center gap-1 text-xs-400 text-muted-foreground">
    <FileText className="w-3 h-3" />
    {part.title || part.filename || 'Document'}
  </div>
)

const FilePart = ({ part }: { part: Extract<Part, { type: 'file' }> }) => {
  if (part.mediaType.startsWith('image/')) {
    return <img src={part.url} alt={part.filename || 'Image'} className="max-w-full rounded-md" />
  }
  return (
    <a
      href={part.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs-400 text-primary hover:underline"
    >
      <FileText className="w-3 h-3" />
      {part.filename || 'File'}
    </a>
  )
}

const PartRenderer = ({ part }: { part: Part }) => {
  switch (part.type) {
    case 'text':
      return <MessageResponse>{part.text}</MessageResponse>
    case 'reasoning':
      return <ReasoningPart part={part} />
    case 'source-url':
      return null // handled separately in SourcesSection
    case 'source-document':
      return <SourceDocumentPart part={part} />
    case 'file':
      return <FilePart part={part} />
    case 'step-start':
      return null
    default:
      if (isToolPart(part)) return <ToolCallPart part={part} />
      return null
  }
}

const GENERAL_WORKING_LABELS = [
  'Thinking...',
  'Working on it...',
  'Looking into it...',
  'On it...',
  'Digging in...',
  'Checking...',
  'Putting it together...',
  'One moment...',
  'Pulling that up...',
  'Figuring it out...',
  'Hang tight...',
  'Crunching the details...',
  'Almost there...',
  'Sorting that out...',
  'Making sense of it...',
  'Just a sec...',
]

const WORKING_LABEL_ROTATE_MS = 2000

// Animated "Buddy is working…" indicator. While a tool with human wording is in
// flight it shows that wording. A tool with none falls through to the general
// phrases rather than its own name — those say something true about the wait
// without naming an internal, so the sidebar never looks frozen and never leaks
// (matches the CLI spinner).
const AssistantWorkingIndicator = ({ toolName }: { toolName?: string }) => {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * GENERAL_WORKING_LABELS.length))
  const toolLabel = toolName ? getToolLabel(toolName, false) : null

  useEffect(() => {
    // Rotate whenever there is no fixed tool label to show — an unlabelled tool
    // gets the general phrases, so it needs the rotation too.
    if (toolLabel) return
    const timer = setInterval(() => {
      setIdx((i) => {
        const next = Math.floor(Math.random() * GENERAL_WORKING_LABELS.length)
        return next === i ? (next + 1) % GENERAL_WORKING_LABELS.length : next
      })
    }, WORKING_LABEL_ROTATE_MS)
    return () => clearInterval(timer)
  }, [toolLabel])

  const label = toolLabel ?? GENERAL_WORKING_LABELS[idx % GENERAL_WORKING_LABELS.length]

  return (
    <div className="py-0.5" data-testid="assistant-working-indicator">
      <Shimmer as="span" className="text-xs-400">
        {label}
      </Shimmer>
    </div>
  )
}

export const AssistantMessage = ({ message, isActive = false }: AssistantMessageProps) => {
  const isUser = message.role === 'user'
  const textContent = getTextContent(message)

  if (isUser) {
    const fileParts = message.parts.filter(
      (p): p is Extract<Part, { type: 'file' }> => p.type === 'file',
    )
    if (!textContent && fileParts.length === 0) return null
    return (
      <Message from="user" data-testid="assistant-user-message">
        <MessageContent>
          {fileParts.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {fileParts.map((file, index) => (
                <img
                  key={file.filename ?? index}
                  src={file.url}
                  alt={file.filename || 'Uploaded image'}
                  className="max-h-32 rounded-md"
                />
              ))}
            </div>
          )}
          {textContent && <span className="whitespace-pre-wrap text-p-400">{textContent}</span>}
        </MessageContent>
      </Message>
    )
  }

  // Buddy is "live" when a text or reasoning part is actively streaming — that
  // content is its own progress signal, so suppress the indicator to avoid a
  // double shimmer.
  const hasLiveStreaming = message.parts.some(
    (p) =>
      (p.type === 'text' || p.type === 'reasoning') &&
      (p as { state?: string }).state === 'streaming',
  )
  // A fired-but-not-returned tool renders nothing on its own (ToolCallPart
  // returns null for 'input-available'); surface the latest one as the label.
  let workingToolName: string | undefined
  for (const part of message.parts) {
    if (isToolUIPart(part) && part.state === 'input-available') {
      workingToolName = getToolName(part)
    }
  }
  const showWorking = isActive && !hasLiveStreaming

  const hasParts = message.parts.length > 0
  if (!hasParts && !showWorking) return null

  const sources = message.parts.filter(
    (p): p is Extract<Part, { type: 'source-url' }> => p.type === 'source-url',
  )
  const nonSourceParts = message.parts.filter((p) => p.type !== 'source-url')

  return (
    <Message from="assistant" data-testid="assistant-bot-message">
      <MessageContent>
        {sources.length > 0 && <SourcesSection sources={sources} />}
        {nonSourceParts.map((part) => (
          <PartRenderer key={part.type} part={part} />
        ))}
        {showWorking && <AssistantWorkingIndicator toolName={workingToolName} />}
      </MessageContent>
      {textContent && (
        <MessageActions>
          <ClipboardCopyButton
            textToCopy={textContent}
            tooltipText="Copy"
            size="iconXs"
            iconSize={14}
            data-testid="assistant-copy-msg-btn"
          />
        </MessageActions>
      )}
    </Message>
  )
}

export const AssistantMessageLoading = () => (
  <Message from="assistant" data-testid="assistant-loading-message">
    <MessageContent>
      <AssistantWorkingIndicator />
    </MessageContent>
  </Message>
)
